import { useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";

const LABEL = "text-sm font-medium text-slate-700 mb-1.5 block";

function Field({ label, children }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      {children}
    </div>
  );
}

export default function AccommodationFormDialog({ room, sites, landlords, onClose, onSaved }) {
  const isEdit = !!room;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vendor_code: room?.vendor_code || "",
    site_id: room?.site_id || "",
    monthly_rent: room?.monthly_rent ?? "",
    rent_start_date: room?.rent_start_date || "",
    cycle_start_day: room?.cycle_start_day ?? "",
    rent_due_day: room?.rent_due_day ?? "",
    deposit_amount: room?.deposit_amount ?? "",
    deposit_paid_date: room?.deposit_paid_date || "",
    deposit_status: room?.deposit_status || "Pending",
    lease_start_date: room?.lease_start_date || "",
    lease_end_date: room?.lease_end_date || "",
    remarks: room?.remarks || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v) => (v === "" || v === null ? null : Number(v));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vendor_code) return toast.error("Landlord is required");
    if (!form.site_id) return toast.error("Assigned Site is required");
    if (form.monthly_rent === "" || Number(form.monthly_rent) <= 0) return toast.error("Monthly Rent Amount must be greater than 0");
    if (!form.rent_start_date) return toast.error("Rent Start Date is required");
    for (const [k, label] of [["cycle_start_day", "Cycle Start Day"], ["rent_due_day", "Rent Due Day"]]) {
      const n = num(form[k]);
      if (n !== null && (!Number.isInteger(n) || n < 1 || n > 31)) return toast.error(`${label} must be between 1 and 31`);
    }
    setSaving(true);
    const body = {
      ...form,
      monthly_rent: Number(form.monthly_rent),
      cycle_start_day: num(form.cycle_start_day),
      rent_due_day: num(form.rent_due_day),
      deposit_amount: num(form.deposit_amount),
      deposit_paid_date: form.deposit_paid_date || null,
      lease_start_date: form.lease_start_date || null,
      lease_end_date: form.lease_end_date || null,
      remarks: form.remarks.trim() || null,
    };
    try {
      const res = isEdit ? await client.put(`/accommodation/rooms/${room.room_id}`, body) : await client.post("/accommodation/rooms", body);
      toast.success(isEdit ? "Accommodation updated" : `Accommodation added — ${res.data.room_id}`);
      onSaved(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="accommodation-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Accommodation — ${room.room_id}` : "Add Accommodation"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Landlord (ACC vendor) *">
              <SearchableSelect testId="accommodation-form-landlord-select" value={form.vendor_code} onValueChange={(v) => set("vendor_code", v)} placeholder="Select landlord"
                options={landlords.map((v) => ({ value: v.vendor_code, label: v.vendor_name, searchText: v.vendor_code, testId: `accommodation-form-landlord-option-${v.vendor_code}` }))} />
            </Field>
            <Field label="Assigned Site *">
              <SearchableSelect testId="accommodation-form-site-select" value={form.site_id} onValueChange={(v) => set("site_id", v)} placeholder="Select site"
                options={sites.map((s) => ({ value: s.site_id, label: s.display_label || s.label, testId: `accommodation-form-site-option-${s.site_id}` }))} />
            </Field>
            <Field label="Monthly Rent Amount (₹) *">
              <Input type="number" step="any" data-testid="accommodation-form-rent-input" value={form.monthly_rent} onChange={(e) => set("monthly_rent", e.target.value)} />
            </Field>
            <Field label="Rent Start Date *">
              <Input type="date" data-testid="accommodation-form-rent-start-input" value={form.rent_start_date} onChange={(e) => set("rent_start_date", e.target.value)} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Cycle Start Day (1-31)">
                <Input type="number" min={1} max={31} data-testid="accommodation-form-cycle-day-input" value={form.cycle_start_day} onChange={(e) => set("cycle_start_day", e.target.value)} />
              </Field>
              <Field label="Rent Due Day (1-31)">
                <Input type="number" min={1} max={31} data-testid="accommodation-form-due-day-input" value={form.rent_due_day} onChange={(e) => set("rent_due_day", e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Field label="Deposit Amount (₹)">
              <Input type="number" step="any" data-testid="accommodation-form-deposit-input" value={form.deposit_amount} onChange={(e) => set("deposit_amount", e.target.value)} />
            </Field>
            <Field label="Deposit Status">
              <Select value={form.deposit_status} onValueChange={(v) => set("deposit_status", v)}>
                <SelectTrigger data-testid="accommodation-form-deposit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Deposit Paid Date">
              <Input type="date" data-testid="accommodation-form-deposit-date-input" value={form.deposit_paid_date} onChange={(e) => set("deposit_paid_date", e.target.value)} />
              {form.deposit_status === "Paid" && !form.deposit_paid_date && <p className="text-xs text-amber-600 mt-1">Paid — consider adding the date.</p>}
            </Field>
            <Field label="Lease Start Date">
              <Input type="date" data-testid="accommodation-form-lease-start-input" value={form.lease_start_date} onChange={(e) => set("lease_start_date", e.target.value)} />
            </Field>
            <Field label="Lease End Date">
              <Input type="date" data-testid="accommodation-form-lease-end-input" value={form.lease_end_date} onChange={(e) => set("lease_end_date", e.target.value)} />
            </Field>
          </div>

          <Field label="Remarks">
            <Textarea data-testid="accommodation-form-remarks-input" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
          </Field>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="accommodation-form-cancel-button">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="accommodation-form-submit-button" className="bg-amber-600 hover:bg-amber-700">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Accommodation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
